import React, { useEffect, useState } from 'react';
import { Modal, Form, Button, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTenant } from '../../settings/TenantContext';
import { supabase } from '../../lib/supabaseClient';

export default function CtaModal({ show, onHide, onSave, tenantId: propTenantId, siteId: propSiteId }) {
  const ctx = useTenant();
  const tenantId = propTenantId || ctx.tenantId;
  const siteId = propSiteId || ctx.siteId;
  const base = process.env.REACT_APP_SKINNY_SUPABASE_URL;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [ctaUrl, setCtaUrl] = useState('');
  const [ctaLabel, setCtaLabel] = useState('Schedule a demo');
  const [ctaLeadThreshold, setCtaLeadThreshold] = useState(0.5);
  const [ctaEnabled, setCtaEnabled] = useState(false);

  const InfoIcon = ({ text }) => (
    <OverlayTrigger placement="right" delay={{ show: 150, hide: 0 }} overlay={<Tooltip className="bg-dark">{text}</Tooltip>}>
      <button type="button" className="btn btn-outline-info btn-sm ms-1 p-0 d-inline-flex align-items-center justify-content-center rounded-circle" style={{ width: 18, height: 18, lineHeight: '16px' }} aria-label="Help">
        <i className="bi bi-info-circle" style={{ fontSize: 12 }} aria-hidden="true"></i>
      </button>
    </OverlayTrigger>
  );

  useEffect(() => {
    (async () => {
      if (!show || !tenantId || !siteId) return;
      setLoading(true);
      setError('');
      try {
        const url = `${(base || '').replace(/\/+$/, '')}/functions/v1/cta-get?tenant_id=${encodeURIComponent(tenantId)}&site_id=${encodeURIComponent(siteId)}`;
        const res = await fetch(url, { method: 'GET' });
        if (!res.ok) {
          if (res.status === 404) {
            setCtaUrl('');
            setCtaLabel('Schedule a demo');
            setCtaLeadThreshold(0.5);
            setCtaEnabled(false);
            setLoading(false);
            return;
          }
          const txt = await res.text().catch(() => '');
          throw new Error(`HTTP ${res.status}${txt ? ` — ${txt.slice(0, 160)}` : ''}`);
        }
        const data = await res.json();
        setCtaUrl(data.cta_url || '');
        setCtaLabel(data.cta_label || 'Schedule a demo');
        setCtaLeadThreshold(data.cta_lead_threshold ?? 0.5);
        setCtaEnabled(data.cta_enabled !== false && !!data.cta_url);
      } catch (e) {
        setError(e?.message || String(e));
      }
      setLoading(false);
    })();
  }, [show, tenantId, siteId, base]);

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      if (ctaUrl && !/^https?:\/\//i.test(ctaUrl.trim())) {
        throw new Error('URL must start with https://');
      }
      const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
      const { error: fnErr } = await supabase.functions.invoke('cta-upsert', {
        body: {
          tenant_id: tenantId,
          site_id: siteId,
          cta_url: ctaEnabled ? (ctaUrl.trim() || null) : null,
          cta_label: ctaLabel.trim() || 'Schedule a demo',
          cta_lead_threshold: Number(ctaLeadThreshold),
          cta_enabled: ctaEnabled,
        },
        headers: tok ? { Authorization: `Bearer ${tok}` } : undefined,
      });
      if (fnErr) throw fnErr;
      onSave && onSave();
      onHide && onHide();
    } catch (e) {
      const msg = (e && e.message) ? String(e.message) : String(e);
      setError(msg);
    }
    setSaving(false);
  }

  return (
    <Modal show={show} onHide={onHide} centered size="md">
      <Modal.Header closeButton>
        <Modal.Title>CTA Button</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
        {loading ? (
          <div className="text-center py-4 text-muted">Loading…</div>
        ) : (
          <>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="cta-enabled-switch"
                label="Enable CTA Button"
                checked={ctaEnabled}
                onChange={(e) => setCtaEnabled(e.target.checked)}
              />
              <Form.Text className="text-muted">When enabled, the AI may show a CTA button in chat responses based on conversation context.</Form.Text>
            </Form.Group>

            <fieldset disabled={!ctaEnabled}>
            <Form.Group className="mb-3">
              <Form.Label>
                Button Label
                <InfoIcon text="The text displayed on the CTA button in the chat window." />
              </Form.Label>
              <Form.Control
                type="text"
                value={ctaLabel}
                onChange={(e) => setCtaLabel(e.target.value)}
                placeholder="Schedule a demo"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                URL
                <InfoIcon text="The link that opens in a new tab when the user clicks the button. Typically a Calendly, HubSpot, or other scheduling link." />
              </Form.Label>
              <Form.Control
                type="url"
                value={ctaUrl}
                onChange={(e) => setCtaUrl(e.target.value)}
                placeholder="https://calendly.com/your-team/demo"
              />
              <Form.Text className="text-muted">Must start with https://</Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Lead Confidence Threshold: <strong>{Number(ctaLeadThreshold).toFixed(1)}</strong>
                <InfoIcon text="The AI will consider showing the CTA button when it estimates the visitor's buying intent is at or above this level (0.0 = always eligible, 1.0 = very high intent only)." />
              </Form.Label>
              <Form.Range
                min={0}
                max={1}
                step={0.1}
                value={ctaLeadThreshold}
                onChange={(e) => setCtaLeadThreshold(parseFloat(e.target.value))}
              />
              <div className="d-flex justify-content-between text-muted" style={{ fontSize: '0.75rem' }}>
                <span>Show more often</span>
                <span>High intent only</span>
              </div>
            </Form.Group>

            {ctaUrl && (
              <div className="mt-3 p-3 border rounded bg-light">
                <div className="text-muted small mb-2">Preview:</div>
                <div>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '8px 16px',
                      borderRadius: 20,
                      background: '#0d6efd',
                      color: '#fff',
                      fontSize: 14,
                      fontWeight: 500,
                      textDecoration: 'none',
                    }}
                  >
                    {ctaLabel || 'Schedule a demo'}
                  </span>
                </div>
              </div>
            )}
            </fieldset>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || loading}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
